import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { ChromePicker } from 'react-color';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    IconButton,
    InputAdornment,
    Popover,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from "@material-ui/core";

import { Colorize } from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { createFunnelCard, updateFunnelCard } from "../../services/funnelCards";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const CardSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "Nome muito curto")
        .required("Obrigatório"),
    type: Yup.string().required("Obrigatório"),
});

const FunnelCardModal = ({ open, onClose, funnelId, card, reload }) => {
    const classes = useStyles();
    const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

    const initialState = {
        name: "",
        color: "#3498db",
        type: "controle",
        percentage: 0,
    };

    const [cardData, setCardData] = useState(initialState);

    useEffect(() => {
        if (card) {
            setCardData({
                name: card.name || "",
                color: card.color || "#3498db",
                type: card.type || "controle",
                percentage: card.percentage || 0,
            });
        } else {
            setCardData(initialState);
        }
    }, [card, open]);

    const handleClose = () => {
        setCardData(initialState);
        setColorPickerAnchor(null);
        onClose();
    };

    const handleSaveCard = async (values) => {
        try {
            const data = {
                ...values,
                funnelId: parseInt(funnelId),
                percentage: values.type === "controle" ? parseInt(values.percentage) : null
            };

            if (card) {
                await updateFunnelCard(card.id, data);
            } else {
                await createFunnelCard(data);
            }
            toast.success(i18n.t("kanban.cards.toasts.success"));
            if (reload) reload();
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {card
                        ? i18n.t("kanban.cards.modal.edit")
                        : i18n.t("kanban.cards.modal.add")}
                </DialogTitle>
                <Formik
                    initialValues={cardData}
                    enableReinitialize={true}
                    validationSchema={CardSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setTimeout(() => {
                            handleSaveCard(values);
                            setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.cards.modal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />

                                <FormControl variant="outlined" margin="dense" fullWidth>
                                    <InputLabel>{i18n.t("kanban.cards.modal.form.type")}</InputLabel>
                                    <Field
                                        as={Select}
                                        label={i18n.t("kanban.cards.modal.form.type")}
                                        name="type"
                                    >
                                        <MenuItem value="controle">{i18n.t("kanban.cards.types.controle")}</MenuItem>
                                        <MenuItem value="conversao">{i18n.t("kanban.cards.types.conversao")}</MenuItem>
                                        <MenuItem value="perda">{i18n.t("kanban.cards.types.perda")}</MenuItem>
                                    </Field>
                                </FormControl>

                                {values.type === "controle" && (
                                    <Field
                                        as={TextField}
                                        label={i18n.t("kanban.cards.modal.form.percentage")}
                                        name="percentage"
                                        type="number"
                                        inputProps={{ min: 0, max: 100 }}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                )}

                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.cards.modal.form.color")}
                                    name="color"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div style={{ backgroundColor: values.color, width: 20, height: 20, borderRadius: 4 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <IconButton
                                                size="small"
                                                color="default"
                                                onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                                            >
                                                <Colorize />
                                            </IconButton>
                                        ),
                                    }}
                                />
                                <Popover
                                    open={Boolean(colorPickerAnchor)}
                                    anchorEl={colorPickerAnchor}
                                    onClose={() => setColorPickerAnchor(null)}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                >
                                    <ChromePicker
                                        color={values.color}
                                        disableAlpha={true}
                                        onChangeComplete={(color) => {
                                            setFieldValue("color", color.hex);
                                        }}
                                    />
                                </Popover>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {card
                                        ? i18n.t("global.buttons.save")
                                        : i18n.t("global.buttons.add")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default FunnelCardModal;
